import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiShield,
  FiTool,
  FiClipboard,
  FiChevronDown,
  FiAlertCircle,
  FiCheckCircle,
  FiSettings,
  FiMessageCircle,
  FiArrowUpRight,
} from "react-icons/fi";

import PageTransition from "./Pagetransition";

const easeOut = [0.22, 1, 0.36, 1];

/* Número de WhatsApp del negocio, mismo usado en el resto del sitio. */
const WHATSAPP_NUMERO = "542236901020";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMERO}?text=${encodeURIComponent(
  "Hola! Tengo una consulta sobre garantías o políticas de devolución."
)}`;

/* ───────────────── DATA ───────────────── */

const SECCIONES = [
  {
    id: "garantia-fabricacion",
    icono: <FiShield />,
    titulo: "Garantía por Defectos de Fábrica",
    resumen: "Cambio o reparación sin costo ante fallas de fabricación.",
    items: [
      {
        subtitulo: "Defectos de fábrica en las aberturas",
        texto:
          "Si la abertura presenta una falla de fabricación, aplica la garantía de la empresa, permitiendo el cambio o reparación sin costo.",
      },
      {
        subtitulo: "Productos a medida",
        texto:
          "Al ser elaboradas bajo especificaciones técnicas propias, estas aberturas no se pueden reintegrar al stock general, por lo que se exceptúan de devoluciones y cambios salvo por fallas de fabricación.",
      },
      {
        subtitulo: "Plazo para cambio de medidas",
        texto:
          "Se tendrá un plazo de 4 días si se necesita cambiar las medidas de la abertura una vez pagada. De ser necesario, se aplicarán los costos adicionales correspondientes.",
      },
    ],
  },
  {
    id: "devoluciones",
    icono: <FiClipboard />,
    titulo: "Políticas de Devolución",
    resumen: "Cómo procedemos ante fallas de funcionamiento o reclamos.",
    items: [
      {
        subtitulo: "Fallas en el funcionamiento",
        texto:
          "Si un producto presenta una falla, es fundamental contactarse con nosotros. Se debe proporcionar una descripción detallada, acompañada de fotos y videos que evidencien el problema. Nuestro equipo evaluará la situación y tomará las medidas necesarias para resolverla.",
      },
      {
        subtitulo: "Conformidad del producto recibido",
        texto:
          "Al firmar el remito, se acepta el producto tal como se entrega. No se aceptarán reclamos relacionados con detalles estéticos, excepto en casos de fallas funcionales como se menciona anteriormente.",
      },
    ],
  },
  {
    id: "ajuste-obra",
    icono: <FiSettings />,
    titulo: "Ajuste en Obra",
    resumen: "Cambios excepcionales de medidas, sujetos a aprobación.",
    items: [
      {
        subtitulo: "Cambios excepcionales",
        texto:
          "Los cambios en las medidas o aperturas pueden ser solicitados, pero deben ser aprobados por nuestro equipo de carpinteros. Estos se realizarán en el depósito.",
      },
    ],
  },
];

/* ───────────────── ACORDEÓN ───────────────── */

function SeccionAcordeon({ seccion, index, abierta, onToggle }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: easeOut }}
      className="group relative overflow-hidden border border-[#c9973a]/10 bg-white/[0.03] backdrop-blur-xl"
    >
      {/* Top line */}
      <div
        className={`pointer-events-none absolute left-0 top-0 z-0 h-[1px] w-full origin-left bg-gradient-to-r from-[#c9973a] via-[#f0c96b] to-transparent transition-transform duration-500 ${
          abierta ? "scale-x-100" : "scale-x-0"
        }`}
      />

      <button
        type="button"
        onClick={onToggle}
        aria-expanded={abierta}
        className="relative z-10 flex w-full items-center justify-between gap-5 px-6 py-6 text-left md:px-9 md:py-7"
      >
        <div className="flex items-center gap-5">
          <div
            className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-sm border text-xl transition-colors duration-300 ${
              abierta
                ? "border-[#c9973a]/40 bg-gradient-to-br from-[#c9973a]/25 to-[#8b3520]/20 text-[#f0c96b]"
                : "border-[#c9973a]/15 text-[#8f8678]"
            }`}
          >
            {seccion.icono}
          </div>

          <div>
            <h3
              className="text-[1.4rem] leading-tight text-[#f9f5ef] md:text-[1.7rem]"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 500,
              }}
            >
              {seccion.titulo}
            </h3>
            <p
              className="mt-1 text-[0.8rem] text-[#8f8678] md:text-[0.85rem]"
              style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
            >
              {seccion.resumen}
            </p>
          </div>
        </div>

        <motion.div
          animate={{ rotate: abierta ? 180 : 0 }}
          transition={{ duration: 0.35, ease: easeOut }}
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border text-base transition-colors duration-300 ${
            abierta
              ? "border-[#c9973a]/40 text-[#f0c96b]"
              : "border-[#c9973a]/15 text-[#8f8678]"
          }`}
        >
          <FiChevronDown />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {abierta && (
          <motion.div
            key="contenido"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: easeOut }}
            className="relative z-10 overflow-hidden"
          >
            <div className="flex flex-col gap-6 px-6 pb-8 pt-1 md:px-9 md:pb-9">
              <div className="h-px w-full bg-gradient-to-r from-[#c9973a]/30 via-[#c9973a]/10 to-transparent" />

              {seccion.items.map((item, i) => (
                <motion.div
                  key={item.subtitulo}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.07, ease: easeOut }}
                  className="flex gap-4"
                >
                  <div className="mt-1.5 flex h-2 w-2 flex-shrink-0 items-center justify-center">
                    <div className="h-[7px] w-[7px] rounded-full bg-[#c9973a] shadow-[0_0_14px_rgba(201,151,58,0.7)]" />
                  </div>
                  <div>
                    <p
                      className="text-[0.95rem] text-[#f0c96b] md:text-[1rem]"
                      style={{
                        fontFamily: "'Jost', sans-serif",
                        fontWeight: 500,
                        letterSpacing: "0.02em",
                      }}
                    >
                      {item.subtitulo}
                    </p>
                    <p
                      className="mt-2 max-w-3xl text-[0.92rem] leading-7 text-[#b7aea2] md:text-[0.97rem] md:leading-8"
                      style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
                    >
                      {item.texto}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Border hover, puramente decorativo */}
      <div className="pointer-events-none absolute inset-0 z-20 border border-transparent transition-all duration-500 group-hover:border-[#c9973a]/15" />
    </motion.div>
  );
}

/* ───────────────── PAGE ───────────────── */

export default function Politicas() {
  const [abiertaId, setAbiertaId] = useState(SECCIONES[0].id);

  function toggleSeccion(id) {
    setAbiertaId((actual) => (actual === id ? null : id));
  }

  return (
    <PageTransition>
      <section className="relative min-h-screen overflow-hidden bg-[#0f0d0b] text-white">
        {/* GRID */}
        <div
          className="
            pointer-events-none absolute inset-0
            bg-[linear-gradient(rgba(201,151,58,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(201,151,58,0.04)_1px,transparent_1px)]
            bg-[size:60px_60px]
          "
        />

        {/* GLOW */}
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="
            pointer-events-none
            absolute
            left-1/2
            top-1/2
            h-[40rem]
            w-[40rem]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-[radial-gradient(circle,rgba(201,151,58,0.1)_0%,transparent_70%)]
          "
        />

        {/* TOP / BOTTOM GLOW */}
        <div className="pointer-events-none absolute left-[-10rem] top-[-10rem] h-[28rem] w-[28rem] rounded-full bg-[#c9973a]/10 blur-[120px]" />
        <div className="pointer-events-none absolute bottom-[-12rem] right-[-10rem] h-[32rem] w-[32rem] rounded-full bg-[#8b3520]/20 blur-[140px]" />

        {/* CONTENT */}
        <div className="relative z-10 mx-auto max-w-5xl px-5 pb-24 pt-28 md:px-8 lg:pt-36">
          {/* HERO */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: easeOut }}
            className="mx-auto max-w-3xl text-center"
          >
            <div className="mb-6 inline-flex items-center gap-3 border border-[#c9973a]/20 bg-[#c9973a]/5 px-5 py-2">
              <div className="h-2 w-2 rounded-full bg-[#c9973a]" />
              <span
                className="text-[0.68rem] uppercase tracking-[0.45em] text-[#c9973a]"
                style={{ fontFamily: "'Jost', sans-serif" }}
              >
                Garantía · Transparencia
              </span>
            </div>

            <h1
              className="text-[3.2rem] leading-[0.95] md:text-[5.5rem]"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 300,
                color: "#f9f5ef",
              }}
            >
              Nuestras
              <br />
              <span
                className="bg-gradient-to-r from-[#f0c96b] via-[#c9973a] to-[#fde68a] bg-clip-text text-transparent"
                style={{ fontWeight: 700 }}
              >
                Políticas
              </span>
            </h1>

            <p
              className="mx-auto mt-8 max-w-2xl text-[1rem] leading-9 text-[#b7aea2] md:text-[1.05rem]"
              style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
            >
              Trabajamos con criterios claros de garantía, devolución y ajuste
              en obra, para que sepas exactamente qué esperar en cada etapa de
              tu compra.
            </p>
          </motion.div>

          {/* SECCIONES */}
          <div className="mt-16 flex flex-col gap-5 md:mt-20">
            {SECCIONES.map((seccion, i) => (
              <SeccionAcordeon
                key={seccion.id}
                seccion={seccion}
                index={i}
                abierta={abiertaId === seccion.id}
                onToggle={() => toggleSeccion(seccion.id)}
              />
            ))}
          </div>

          {/* NOTA / DISCLAIMER */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: easeOut }}
            className="mt-10 flex items-start gap-4 border border-[#c9973a]/10 bg-[#c9973a]/[0.03] px-6 py-5 md:px-7"
          >
            <FiAlertCircle className="mt-0.5 flex-shrink-0 text-lg text-[#c9973a]" />
            <p
              className="text-[0.85rem] leading-7 text-[#8f8678]"
              style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
            >
              Estas políticas buscan proteger tanto la calidad del servicio
              como la inversión que hacés en tu hogar o proyecto. Ante
              cualquier duda, nuestro equipo está disponible para asesorarte
              antes y después de la compra.
            </p>
          </motion.div>

          {/* CTA contacto */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: easeOut }}
            className="relative mt-20 overflow-hidden border border-[#c9973a]/10 bg-white/[0.03] px-8 py-12 text-center backdrop-blur-xl md:px-14 md:py-16"
          >
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#c9973a]/10 via-transparent to-[#8b3520]/15" />

            <div className="relative z-10 flex flex-col items-center gap-6">
              <div className="flex h-14 w-14 items-center justify-center rounded-full border border-[#c9973a]/30 bg-gradient-to-br from-[#c9973a]/20 to-[#8b3520]/20 text-2xl text-[#f0c96b]">
                <FiCheckCircle />
              </div>

              <h2
                className="max-w-xl text-[2rem] leading-none text-[#f9f5ef] md:text-[2.8rem]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                ¿Tenés dudas sobre tu garantía?
              </h2>

              <p
                className="max-w-md text-[0.95rem] leading-7 text-[#b7aea2]"
                style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
              >
                Escribinos y te ayudamos a resolverlo lo antes posible.
              </p>

              <motion.a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.97 }}
                className="relative z-10 mt-2 flex items-center gap-2.5 bg-gradient-to-r from-[#8b6220] via-[#c9973a] to-[#f0c96b] px-8 py-3.5 text-[#1a1814] shadow-lg shadow-[#c9973a]/10 transition-shadow hover:shadow-[#c9973a]/25"
              >
                <FiMessageCircle className="text-lg" />
                <span
                  className="text-[0.78rem] font-semibold uppercase tracking-[0.25em]"
                  style={{ fontFamily: "'Jost', sans-serif" }}
                >
                  Hablar con nosotros
                </span>
                <FiArrowUpRight className="text-base" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </PageTransition>
  );
}